<!-- The script editor's Fountain mode (#scriptFountainWrap, shown when the editor store's shownMode is 'fountain'): a
     screenplay written in Fountain, the open plain-text screenplay format. Bar: the element of the line with the cursor
     (a menu to change it), the keys, Write / Preview / Side by side (side by side from 1280 px), the panels button
     (below 1024 px) and PDF with its paper (US Letter or A4). Left: the scene navigator (sections and scenes, numbered;
     tap = go there) and the characters with how many speeches each has. Middle: the text field #scriptFountain (read
     and written by the editor store like the Markdown field; it never draws in it) and/or the preview, the pages in the
     industry layout (fountainHtml.ts, .fn-* in legacy.css: white paper on purpose, it shows the printed page).
     Keys in the field: Tab / Shift+Tab change the line's element (convertLine), Enter starts the element that follows
     (nextKind: a cue → dialogue, dialogue → next cue, a scene heading → action…), Shift+Enter is a plain new line;
     scene headings and cues are written in capitals as they are typed; suggestions (character names, INT./EXT., places,
     times of day, transitions) open under the cursor: arrows, Enter or Tab to take one, Esc to close.
     Logic without UI: src/features/editor/fountain.ts, fountainWrite.ts, fountainHtml.ts, fountainPrint.ts. -->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { hasFiles } from '../../utils/image'
import { useI18n } from '../../composables/useI18n'
import { useEditorStore } from '../../stores/editor'
import { uiAlert } from '../../stores/notices'
import { fountainCharacters, fountainScenes, parseFountain, type FountainDoc, type FountainKind } from '../../features/editor/fountain'
import { screenplayHtml } from '../../features/editor/fountainHtml'
import {
  ELEMENTS, caretKind, convertLine, cycleKind, lineAt, lineKinds, lineStart, nextKind, suggestions, type Suggestion, type TextEdit
} from '../../features/editor/fountainWrite'
import { isTextFile } from '../../features/drop'
import type { Paper } from '../../features/editor/fountainPrint'

const { t, tn, lang } = useI18n()
const ed = useEditorStore()

/** Names of the elements (the screenplay terms) */
const NAMES: Record<FountainKind, string> = {
  scene: t('Scene heading'), action: t('Action'), character: t('Character'), parenthetical: t('Parenthetical'), dialogue: t('Dialogue'),
  transition: t('Transition'), centered: t('Centered'), lyrics: t('Lyrics'), section: t('Section'), synopsis: t('Synopsis'), note: t('Note'),
  pageBreak: t('Page break'), boneyard: t('Note')
}

const ta = ref<HTMLTextAreaElement | null>(null)
const preview = ref<HTMLElement | null>(null)
const doc = shallowRef<FountainDoc>(parseFountain(''))
const html = ref('')
/** Write / Preview / Side by side (side by side only from 1280 px: below it, it shows the text field) */
const view = ref<'write' | 'preview' | 'split'>(innerWidth >= 1280 ? 'split' : 'write')
const navOpen = ref(false)
const paper = ref<Paper>(lang === 'it' ? 'a4' : 'letter')
/** The element chosen for the line with the cursor (Tab, Enter, the menu), until the cursor leaves that line */
let pending: { line: number; kind: FountainKind } | null = null
const kind = ref<FountainKind>('action')
const sugg = shallowRef<Suggestion[]>([])
const suggAt = ref(0)
/** Where the suggestions open: under the cursor, or above it near the bottom of the field */
const suggPos = ref<{ top?: number; bottom?: number; left: number }>({ top: 0, left: 0 })

const scenes = computed(() => fountainScenes(doc.value))
const characters = computed(() => fountainCharacters(doc.value))
const src = () => ta.value?.value ?? ''

// ----- Reading the text again -----
let htmlTimer: ReturnType<typeof setTimeout> | undefined
/** The text changed (or was written by the store): parsed again; the preview 200 ms after the last change. In the
 *  Write view the preview is hidden and not drawn: the Preview and Side by side buttons draw it (reparse(true)) */
function reparse(now = false): void {
  doc.value = parseFountain(src())
  clearTimeout(htmlTimer)
  if (view.value === 'write') return
  const draw = () => { html.value = screenplayHtml(doc.value, { screen: true }) }
  if (now) draw(); else htmlTimer = setTimeout(draw, 200)
}
watch(() => ed.fountainSeq, () => { pending = null; reparse(true); caretChanged(); closeSugg() })

// ----- The element at the cursor -----
const caretLine = () => lineAt(src(), ta.value?.selectionStart ?? 0)
/** The cursor moved or the text changed: the element shown; leaving the line forgets the chosen element */
function caretChanged(): void {
  const ln = caretLine()
  if (pending && pending.line !== ln.index) pending = null
  kind.value = caretKind(src(), doc.value, ln.index, pending ? pending.kind : null)
}

// ----- Changing the text (with the browser's undo when it can) -----
let applying = false
function apply(e: TextEdit): void {
  const el = ta.value
  if (!el) return
  applying = true
  el.focus()
  el.setSelectionRange(e.start, e.end)
  if (!document.execCommand('insertText', false, e.text)) el.setRangeText(e.text, e.start, e.end, 'end')
  el.setSelectionRange(e.caret, e.caret)
  applying = false
  changed()
}
function changed(): void {
  ed.queueSave()
  reparse()
  caretChanged()
}
/** Turns the line with the cursor into an element; the element stays chosen while the cursor is on it */
function setKind(k: FountainKind): void {
  const s = src(), ln = caretLine()
  apply(convertLine(s, ln.index, k))
  pending = { line: caretLine().index, kind: k }
  caretChanged(); updateSugg()
}

// ----- Typing -----
function onInput(e: Event): void {
  if (applying) return
  changed()
  // Scene headings, cues and transitions in capitals, as they are typed
  const el = ta.value!, ie = e as InputEvent
  if (!ie.isComposing && (kind.value === 'scene' || kind.value === 'character' || kind.value === 'transition')) {
    const ln = caretLine(), up = ln.text.toUpperCase(), pos = el.selectionStart
    if (up !== ln.text && up.length === ln.text.length) { apply({ start: ln.start, end: ln.end, text: up, caret: pos }); pending = pending || { line: ln.index, kind: kind.value } }
  }
  updateSugg()
}
function onKey(e: KeyboardEvent): void {
  const el = ta.value!
  if (sugg.value.length) {
    if (/^(ArrowLeft|ArrowRight|Home|End|PageUp|PageDown)$/.test(e.key)) closeSugg()
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); suggAt.value = (suggAt.value + (e.key === 'ArrowDown' ? 1 : -1) + sugg.value.length) % sugg.value.length; return }
    // Enter takes one; Tab too, once something is typed (on an empty line Tab still changes the element)
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey && caretLine().text.trim())) { e.preventDefault(); takeSugg(suggAt.value); return }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeSugg(); return }
  }
  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (e.key === 'Tab') {
    e.preventDefault()
    setKind(cycleKind(kind.value, e.shiftKey ? -1 : 1))
    return
  }
  if (e.key === 'Enter' && !e.shiftKey && el.selectionStart === el.selectionEnd) {
    const ln = caretLine()
    if (el.selectionStart !== ln.end) return   // in the middle of a line: a plain new line
    if (!ln.text.trim()) {
      // Enter on an empty cue, heading or transition: it becomes action, like leaving the element
      if (kind.value !== 'action' && kind.value !== 'dialogue') { e.preventDefault(); pending = { line: ln.index, kind: 'action' }; caretChanged(); closeSugg() }
      return
    }
    e.preventDefault()
    const cur = kind.value, detected = lineKinds(doc.value, src().split('\n').length)[ln.index]
    // The chosen element written with its marks before leaving the line (a cue still without dialogue reads as one)
    if (pending && detected !== cur && !(cur === 'character' && caretKind(src(), doc.value, ln.index, null) === 'character')) apply(convertLine(src(), ln.index, cur))
    const nx = nextKind(cur), p = ta.value!.selectionStart
    apply({ start: p, end: p, text: nx.breaks, caret: p + nx.breaks.length })
    pending = { line: caretLine().index, kind: nx.kind }
    caretChanged(); updateSugg()
  }
}

// ----- Suggestions -----
function closeSugg(): void { sugg.value = []; suggAt.value = 0 }
function updateSugg(): void {
  const ln = caretLine(), el = ta.value
  const list = el && el.selectionStart === el.selectionEnd && el.selectionStart === ln.end ? suggestions(doc.value, kind.value, ln.text) : []
  sugg.value = list; suggAt.value = 0
  if (list.length && el) {
    const c = caretXY(el, el.selectionStart)
    suggPos.value = c.top + 240 > el.clientHeight && c.top > 260 ? { bottom: el.clientHeight - c.top + c.line + 4, left: c.left } : { top: c.top + 4, left: c.left }
  }
}
function takeSugg(i: number): void {
  const s = sugg.value[i], ln = caretLine()
  if (!s) return
  const k = kind.value
  apply({ start: ln.start, end: ln.end, text: s.line, caret: ln.start + s.line.length })
  pending = { line: ln.index, kind: k }
  caretChanged(); updateSugg()
}
/** Where the cursor is in the field (a hidden copy of the field measures it), relative to the field's box */
function caretXY(el: HTMLTextAreaElement, pos: number): { top: number; left: number; line: number } {
  const cs = getComputedStyle(el), m = document.createElement('div')
  for (const p of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight', 'padding', 'border', 'boxSizing', 'whiteSpace', 'wordWrap', 'overflowWrap', 'tabSize'] as const) (m.style as unknown as Record<string, string>)[p] = cs[p] as string
  m.style.cssText += `;position:absolute;visibility:hidden;white-space:pre-wrap;overflow-wrap:break-word;width:${el.clientWidth}px;top:0;left:0`
  m.textContent = el.value.slice(0, pos)
  const mark = document.createElement('span'); mark.textContent = '​'; m.appendChild(mark)
  document.body.appendChild(m)
  const r = { top: mark.offsetTop + mark.offsetHeight - el.scrollTop, left: Math.max(8, Math.min(mark.offsetLeft, el.clientWidth - 220)), line: mark.offsetHeight }
  m.remove()
  return r
}

// ----- Navigator -----
/** Puts the cursor at the start of line n, in the field and in the preview */
function goLine(n: number): void {
  const el = ta.value
  if (el && view.value !== 'preview') {
    const p = lineStart(src(), n)
    el.focus({ preventScroll: true }); el.setSelectionRange(p, p)
    el.scrollTop = Math.max(0, caretXY(el, p).top + el.scrollTop - 80)
    caretChanged()
  }
  const box = preview.value
  if (box && view.value !== 'write') {
    const all = [...box.querySelectorAll<HTMLElement>('[data-line]')], hit = all.filter(x => +x.dataset.line! <= n).pop() || all[0]
    hit?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  if (innerWidth < 1024) navOpen.value = false
}
/** A tap on the preview: the field's cursor goes to that element (side by side) */
function previewClick(e: MouseEvent): void {
  const hit = (e.target as HTMLElement).closest<HTMLElement>('[data-line]')
  if (hit && view.value === 'split') goLine(+hit.dataset.line!)
}
const firstCue = (name: string) => doc.value.tokens.find(tk => tk.kind === 'character' && (tk.name || '').toUpperCase() === name)

// ----- A file dropped on the field: inserted at the cursor -----
async function onDrop(e: DragEvent): Promise<void> {
  const f = e.dataTransfer && e.dataTransfer.files[0]
  if (!f) return
  e.preventDefault()
  if (!isTextFile(f)) { uiAlert(t('In the script you can drop a text file (.txt or .md).')); return }
  const text = (await f.text()).replace(/\r\n?/g, '\n'), el = ta.value!, p = el.selectionStart
  apply({ start: p, end: el.selectionEnd, text, caret: p + text.length })
}
function onDragOver(e: DragEvent): void { if (hasFiles(e)) e.preventDefault() }

// Esc closes the panels opened over the page (below 1024 px) before the editor
function escPanels(e: KeyboardEvent): void { if (e.key === 'Escape' && navOpen.value) { e.stopPropagation(); e.preventDefault(); navOpen.value = false } }
onMounted(() => window.addEventListener('keydown', escPanels, true))
onBeforeUnmount(() => { window.removeEventListener('keydown', escPanels, true); clearTimeout(htmlTimer) })
watch(() => ed.shownMode, m => { if (m === 'fountain') nextTick(() => { reparse(true); caretChanged() }); else { closeSugg(); navOpen.value = false } })

const viewBtn = (on: boolean) => 'px-3 py-1 rounded-md ' + (on ? 'bg-brand text-white' : 'text-gray-400')
const sceneCount = computed(() => scenes.value.filter(s => s.kind === 'scene').length)
</script>

<template>
  <div v-show="ed.shownMode === 'fountain'" id="scriptFountainWrap" class="flex-1 min-h-0 flex flex-col">
    <div class="flex items-center gap-2 px-3 md:px-6 py-2 border-b border-gray-800 bg-darkcard text-xs flex-wrap">
      <button type="button" :title="t('Scenes and characters')" class="lg:hidden px-2 py-1 rounded-md text-gray-400 hover:text-brand" @click="navOpen = !navOpen"><i class="fas fa-list"></i></button>
      <label class="flex items-center gap-1.5 text-gray-400"><span class="hidden sm:inline">{{ t('Element') }}</span>
        <select id="fnElement" :value="kind" class="bg-darkinput border border-gray-800 rounded-md px-2 py-1 text-gray-100 font-semibold focus:outline-none focus:border-brand" @mousedown="closeSugg()" @change="setKind(($event.target as HTMLSelectElement).value as FountainKind)">
          <option v-for="k in ELEMENTS" :key="k" :value="k">{{ NAMES[k] }}</option>
        </select></label>
      <span class="hidden xl:inline text-gray-500">{{ t('Tab: next element · Shift+Tab: previous · Enter: what comes next · Shift+Enter: new line') }}</span>
      <div class="ml-auto flex bg-darkbtn rounded-lg p-0.5 font-bold" role="tablist">
        <button type="button" role="tab" :aria-selected="view === 'write'" :class="viewBtn(view === 'write')" @click="view = 'write'">{{ t('Write') }}</button>
        <button type="button" role="tab" :aria-selected="view === 'preview'" :class="viewBtn(view === 'preview')" @click="view = 'preview'; reparse(true)">{{ t('Preview') }}</button>
        <button type="button" role="tab" :aria-selected="view === 'split'" :class="['hidden xl:block', viewBtn(view === 'split')]" @click="view = 'split'; reparse(true)">{{ t('Side by side') }}</button>
      </div>
      <select v-model="paper" :title="t('Paper size')" class="bg-darkinput border border-gray-800 rounded-md px-1.5 py-1 text-gray-300 focus:outline-none focus:border-brand">
        <option value="letter">US Letter</option><option value="a4">A4</option>
      </select>
      <button type="button" :title="t('Print or save as PDF')" class="px-3 py-1.5 rounded-lg bg-darkbtn text-gray-300 hover:text-gray-100 font-bold" @click="ed.printPdf(paper)"><i class="fas fa-file-pdf mr-1"></i>PDF</button>
    </div>
    <div class="flex-1 min-h-0 flex">
      <!-- Scenes and characters (from 1024 px on the left; below it over the page) -->
      <aside :class="['script-toc fn-nav hidden lg:block w-60 flex-shrink-0 overflow-y-auto px-3 py-4 border-r border-gray-800', { 'toc-open': navOpen }]">
        <div class="flex items-center justify-between px-2 mb-2"><span class="text-[11px] font-bold uppercase tracking-wider text-gray-500"><i class="fas fa-clapperboard mr-1.5"></i>{{ t('Scenes') }} <span v-if="sceneCount" class="font-normal">· {{ sceneCount }}</span></span>
          <button type="button" :title="t('Close')" class="lg:hidden text-gray-500 hover:text-gray-100" @click="navOpen = false"><i class="fas fa-xmark"></i></button></div>
        <template v-if="scenes.length">
          <button v-for="(s, i) in scenes" :key="i + ':' + s.line" type="button" :title="s.text" :class="['toc-it', s.kind === 'section' ? 'toc-1' : 'toc-2']" @mousedown.prevent @click="goLine(s.line)">
            <span v-if="s.kind === 'scene'" class="text-gray-500 mr-1.5">{{ s.number }}</span>{{ s.text }}</button>
        </template>
        <p v-else class="px-2 text-xs text-gray-500 leading-relaxed">{{ t('Scene headings (INT. / EXT.) and sections (#) appear here: tap one to jump to it.') }}</p>
        <div class="flex items-center px-2 mt-5 mb-2"><span class="text-[11px] font-bold uppercase tracking-wider text-gray-500"><i class="fas fa-user mr-1.5"></i>{{ t('Characters') }}</span></div>
        <template v-if="characters.length">
          <button v-for="c in characters" :key="c.name" type="button" :title="t('Go to their first line')" class="toc-it toc-2" @mousedown.prevent @click="firstCue(c.name) && goLine(firstCue(c.name)!.line)">
            {{ c.name }}<span class="ml-2 text-[11px] text-gray-500">{{ tn(c.speeches, '{n} line', '{n} lines') }}</span></button>
        </template>
        <p v-else class="px-2 text-xs text-gray-500 leading-relaxed">{{ t('The characters who speak appear here, with how many lines each one has.') }}</p>
      </aside>
      <!-- The text -->
      <div v-show="view !== 'preview'" role="tabpanel" :class="['relative flex-1 min-w-0 flex', { 'xl:max-w-[50%]': view === 'split' }]">
        <textarea id="scriptFountain" ref="ta" spellcheck="true" :placeholder="t('Write your screenplay here. Start with a scene heading, such as INT. KITCHEN - NIGHT')"
          class="fn-source select-text flex-1 w-full max-w-3xl mx-auto px-4 md:px-8 py-6 bg-transparent text-gray-100 text-[15px] leading-relaxed focus:outline-none resize-none"
          @input="onInput" @keydown="onKey" @click="caretChanged(); closeSugg()" @keyup="caretChanged()" @blur="closeSugg()" @drop="onDrop" @dragover="onDragOver"></textarea>
        <ul v-if="sugg.length" class="fn-sugg absolute z-[62] min-w-[12rem] max-w-[20rem] bg-darkcard border border-gray-800 rounded-lg shadow-xl py-1 text-sm" :style="{ top: suggPos.top != null ? suggPos.top + 'px' : 'auto', bottom: suggPos.bottom != null ? suggPos.bottom + 'px' : 'auto', left: suggPos.left + 'px' }">
          <li v-for="(s, i) in sugg" :key="s.label"><button type="button" :class="['w-full text-left px-3 py-1.5 truncate', i === suggAt ? 'bg-brand/15 text-brand' : 'text-gray-200 hover:bg-darkbtn']" @mousedown.prevent="takeSugg(i)">{{ s.label }}</button></li>
        </ul>
      </div>
      <!-- The pages -->
      <div v-show="view !== 'write'" ref="preview" role="tabpanel" :class="['fn-scroll flex-1 min-w-0 overflow-y-auto px-3 md:px-6 py-6', { 'hidden xl:block': view === 'split' }]" @click="previewClick">
        <div class="fn-paper" v-html="html"></div>
        <p v-if="!html" class="text-center text-sm text-gray-500 mt-10">{{ t('The formatted screenplay appears here.') }}</p>
      </div>
    </div>
  </div>
</template>
